import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown } from 'lucide-react';
import { cn } from '../../utils/cn';
import { hexToRgba } from '../../utils/color';
import { CATEGORIES, CategorySlug, CategoryDefinition } from '../../config/categoryConfig';
import { StockCategoryFilter } from '../../types/stockCategories';
import { useCategoryLocking } from '../../hooks/useCategoryLocking/index';

interface SubHeaderCategoriesProps {
  currentCategory?: StockCategoryFilter;
  className?: string;
}

/**
 * Get button styles based on category color and active state
 */
function getButtonStyles(category: CategoryDefinition, isActive: boolean, isHovered: boolean) {
  const baseColor = category.color;

  if (isActive) {
    return {
      backgroundColor: baseColor,
      color: category.textColor || '#ffffff',
      boxShadow: `0 0 0 2px ${hexToRgba(baseColor, 0.4)}`,
    };
  }

  return {
    backgroundColor: hexToRgba(baseColor, isHovered ? 0.3 : 0.15),
    color: baseColor,
  };
}

/**
 * SubHeaderCategories component - Desktop only
 *
 * Displays a compact grid of category buttons with emoji + name layout.
 */
export const SubHeaderCategories: React.FC<SubHeaderCategoriesProps> = ({
  currentCategory,
  className
}) => {
  const navigate = useNavigate();
  const [hoveredCategory, setHoveredCategory] = React.useState<string | null>(null);
  const { isCategoryLocked, CategoryLockModal } = useCategoryLocking();

  const handleCategoryClick = (category: CategoryDefinition) => {
    navigate(`/${category.slug as CategorySlug}`);
  };

  return (
    <div
      data-tutorial-id="categories-section"
      className={cn(
        "flex",
        "w-full justify-center",
        className
      )}
    >
      <div className="flex items-center gap-1.5">
        <span className="hidden lg:inline italic text-xs text-block-500 dark:text-white-400 whitespace-nowrap">
          Try other categories:
        </span>
        {CATEGORIES.map((category) => {
          const isActive = currentCategory === category.slug;
          const isHovered = hoveredCategory === category.slug;
          const buttonStyles = getButtonStyles(category, isActive, isHovered);
          const isLocked = isCategoryLocked(category.slug as StockCategoryFilter);

          return (
            <button
              key={category.slug}
              onClick={() => handleCategoryClick(category)}
              onMouseEnter={() => setHoveredCategory(category.slug)}
              onMouseLeave={() => setHoveredCategory(null)}
              className={cn(
                "relative flex flex-col items-center justify-center",
                "w-7 h-7 lg:w-[5rem] lg:h-auto rounded-full lg:rounded-md lg:py-0.5",
                "transition-all duration-200 ease-in-out",
                "focus:outline-none focus:ring-2 focus:ring-offset-1",
                "hover:scale-110 lg:hover:scale-150 active:scale-95",
                isLocked && 'opacity-60'
              )}
              style={buttonStyles}
              aria-label={`Switch to ${category.displayName} category`}
              aria-current={isActive ? 'page' : undefined}
              title={category.description}
              aria-disabled={false}
            >
              <span className="text-sm lg:text-xs leading-none" aria-hidden="true">
                {category.icon}
              </span>
              <span className="hidden lg:inline text-xs font-semibold leading-none mt-px truncate w-full text-center">
                {category.shortName || category.displayName}
              </span>
              {category.isPremium && (
                <Crown className="hidden lg:block absolute top-0 right-0.5 h-2.5 w-2.5 text-yellow-500 dark:text-yellow-400" aria-label="Premium" />
              )}
            </button>
          );
        })}
      </div>
      {CategoryLockModal}
    </div>
  );
};

export default SubHeaderCategories;
